import type { FC } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@components/atoms/icon';
import { MiniArrowIcon } from '@components/icons/mini-arrow-icon';
import type { SelfieRequirementsPageProps } from '@modules/widget/pages/selfie-requirements-page/types';
import { RequirementsPageContainer } from '@modules/widget/pages/styles';

export const SelfieRequirementsPage: FC<SelfieRequirementsPageProps> = ({ onBack }) => {
    const { t } = useTranslation();

    const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);

    const cards = useMemo(
        () => [
            {
                title: t('requirementsPage.selfie.editedPhotoTitle'),
                description: t('requirementsPage.selfie.editedPhotoDescription'),
                img: 'https://static.dataspike.io/images/sdk/selfie/edit-photo.png',
            },
            {
                title: t('requirementsPage.selfie.darkPhotoTitle'),
                description: t('requirementsPage.selfie.darkPhotoDescription'),
                img: 'https://static.dataspike.io/images/sdk/selfie/dark-photo.png',
            },
            {
                title: t('requirementsPage.selfie.croppedPhotoTitle'),
                description: t('requirementsPage.selfie.croppedPhotoDescription'),
                img: 'https://static.dataspike.io/images/sdk/selfie/cropped-photo.png',
            },
            {
                title: t('requirementsPage.selfie.photoWithAccessoriesTitle'),
                description: t('requirementsPage.selfie.photoWithAccessoriesDescription'),
                img: 'https://static.dataspike.io/images/sdk/selfie/photo-with-accessories.png',
            },
        ],
        [t],
    );

    const handleBackClick = () => {
        if (onBack) {
            onBack();
        } else {
            window.history.back();
        }
    };

    return (
        <RequirementsPageContainer isUseWebSDK={!!onBack}>
            {searchParams.get('hideHeader') === 'true' ? (
                <React.Fragment />
            ) : (
                <div className='page-header'>
                    <Icon
                        icon={<MiniArrowIcon className='arrow-icon' width={18} height={14} />}
                        $clickable={true}
                        onClick={handleBackClick}
                    />
                    <h2 className='page-title'>{t('requirementsPage.selfie.title')}</h2>
                    <div className='page-hidden-elem' />
                </div>
            )}
            <div
                className='page-description'
                dangerouslySetInnerHTML={{ __html: t('requirementsPage.selfie.description') }}
            />
            <div className='card-container'>
                {cards.map((card, index) => (
                    <React.Fragment key={card.title}>
                        <div className='card'>
                            <img src={card.img} alt={card.title} className='card-img' />
                            <div className='card-title'>{card.title}</div>
                            <div className='card-description'>{card.description}</div>
                        </div>
                        {index % 2 === 1 && index !== cards.length - 1 && (
                            <div className='divider' />
                        )}
                    </React.Fragment>
                ))}
            </div>
        </RequirementsPageContainer>
    );
};
